import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAdminRole } from "@/hooks/useAdminRole";

/**
 * Hard route guard for /admin/* — never renders children until the role
 * check resolves AND the user is confirmed as admin. Non-admins are
 * redirected away without ever mounting the admin UI.
 *
 * Note: data access is also enforced server-side by RLS; this guard is a
 * defense-in-depth measure that prevents inspection of admin UI structure.
 */
export const RequireAdmin = ({ children }: { children: ReactNode }) => {
  const { isAdmin, isLoading } = useAdminRole();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-md p-8">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="h-64 w-full bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default RequireAdmin;
